/**
 * The results grid's "Add to WHERE" in the SQL view: the condition a cell
 * stands for, written into the statement that produced the result.
 *
 * The condition is the one Table Explorer's "Add to filter" starts from
 * (`pickCellFilterOperator`: NULL → IS NULL, an array cell → contains its first
 * element, otherwise `=`), written by the shared filter emitter
 * (`buildConditionSql`) with the column's data type — so it takes the same
 * literal policies as the filter chips: MySQL text that survives backslash
 * escapes (`quoteMysqlText`), the engine's date literal (`sqlTemporalLiteral`,
 * Oracle `TO_DATE`), boolean spelling (`sqlBooleanLiteral`). The field is the
 * column as the statement names its source (`resultColumnWhereField`).
 *
 * The condition goes in through the resolver's add-where planner — the edit
 * the Visual view's filter chips make — so both views agree on where a WHERE
 * goes (before GROUP BY / ORDER BY / LIMIT / WINDOW / `FOR UPDATE` / MySQL
 * `LOCK IN SHARE MODE` / SQL Server `OPTION (…)`, whatever the spacing, as in
 * `WHERE(a = 1)`), skip strings, comments and subqueries, and keep an OR
 * clause's meaning. The SQL view only lays the new `WHERE …` / `AND …` on a
 * line of its own (`onItsOwnLine`).
 *
 * Pure: the caller picks the text (selection, last partial run, or buffer) and
 * applies the result.
 */

import type { DatabaseEngineId } from '../../../../core/engines';
import { databaseEngineIdToSqlEngine } from '../../../../core/engines/databaseEngineIdToSqlEngine';
import { buildParsedDocumentFromSource, getDocumentStructure } from '../../../sql-grammar/document';
import { buildLineIndex, rangeFromOffsets, type LineIndex } from '../../../sql-grammar/document/lineIndex';
import type { ParseOptions, SqlEngine, StatementInfo } from '../../../sql-grammar/model';
import { applyStructuralEdit } from '../../../sql-resolver/edit/applyStructuralEdit';
import { emitSource } from '../../../sql-resolver/edit/emitSource';
import { findEnclosingComment } from '../../../sql-resolver/edit/emitSource.edges';
import { extractQueryStructure } from '../../../sql-resolver/structure/extractQueryStructure';
import { trimRange } from '../../../sql-resolver/structure/scanner';
import { catalogIdentSql } from '../../../sql-resolver/suggest/sqlIdentifierText';
import type { ParsedBuffer, PlannedByteEdit, QueryStructure, SchemaSnapshot } from '../../../sql-resolver/types';
import type { FilterCondition } from '../../core/types/workspaceTypes';
import { vqeParseOptionsFor } from '../../visual-query-editor/workspace/classifyTabQuery';
import { pickCellFilterOperator } from './cellFilterOperator';
import type { FilterOperator } from './filterUtils';
import { buildConditionSql } from './filterWhereBuilder';
import { resultColumnWhereField, type ResultColumnField, type ResultColumnTarget } from './resultColumnWhereField';

export type AddToWherePlan =
  | { readonly kind: 'edit'; readonly text: string; readonly operator: FilterOperator }
  /** An aggregate / window result: WHERE cannot filter it. */
  | { readonly kind: 'unfilterable' }
  /** Which source column the result column reads is not known (a snapshot may tell). */
  | { readonly kind: 'unknown' }
  /** Not a SELECT / UPDATE / DELETE the resolver reads (a procedure call, Kusto): no WHERE to add to. */
  | { readonly kind: 'unsupported-statement' }
  /**
   * A SELECT inside IF / WHILE / BEGIN … END or a DO block: the resolver reads
   * no structure inside control flow (the whole `IF … SELECT …` is one
   * statement of unknown shape), so nothing says where its WHERE goes.
   */
  | { readonly kind: 'control-flow' };

type Statement = {
  readonly buffer: ParsedBuffer;
  readonly info: StatementInfo;
  readonly options: ParseOptions;
  readonly structure: QueryStructure;
};

const UNSUPPORTED: AddToWherePlan = { kind: 'unsupported-statement' };
const CONTROL_FLOW = { kind: 'control-flow' } as const satisfies AddToWherePlan;

/** The starting condition for a cell of `column` — Table Explorer's "Add to filter" choice. */
export function cellFilterCondition(value: unknown, column: Pick<ResultColumnTarget, 'dataType' | 'isForeignKey'>): FilterCondition {
  // A 64-bit integer cell can arrive as a BigInt, which the filter value (and JSON) cannot hold.
  const cell = typeof value === 'bigint' ? (Number.isSafeInteger(Number(value)) ? Number(value) : String(value)) : value;
  const choice = pickCellFilterOperator(cell, { dataType: column.dataType ?? '', isForeignKey: !!column.isForeignKey });
  return choice.value === undefined ? { operator: choice.operator } : { operator: choice.operator, value: choice.value };
}

export function planAddToWhere(
  sql: string,
  target: ResultColumnTarget,
  value: unknown,
  engineId: DatabaseEngineId,
  snapshot?: SchemaSnapshot | null,
): AddToWherePlan {
  const statement = lastStatement(sql, engineId);
  if (!statement) return UNSUPPORTED;
  if ('kind' in statement) return statement;
  const field = statementField(statement, target, snapshot);
  if (field.kind !== 'field') return field;
  const condition = cellFilterCondition(value, target);
  const predicate = buildConditionSql(field.sql, condition, engineId, { valueDataType: target.dataType });
  const text = addWherePredicate(statement, predicate);
  return text === null ? UNSUPPORTED : { kind: 'edit', text, operator: condition.operator };
}

/** The text's last statement, when it is a SELECT, UPDATE or DELETE; `control-flow` when it is IF / WHILE / a block. */
function lastStatement(sql: string, engineId: DatabaseEngineId): Statement | typeof CONTROL_FLOW | null {
  if (engineId !== 'mock' && !databaseEngineIdToSqlEngine(engineId)) return null;
  const options = vqeParseOptionsFor(engineId);
  try {
    const parsedDocument = buildParsedDocumentFromSource(sql, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'add-to-where');
    const info = documentStructure.statements[documentStructure.statements.length - 1];
    if (info && isControlFlow(sql, info, options)) return CONTROL_FLOW;
    if (!info || (info.kind !== 'select' && info.kind !== 'update' && info.kind !== 'delete')) return null;
    const buffer: ParsedBuffer = { parsedDocument, documentStructure };
    return { buffer, info, options, structure: extractQueryStructure(buffer, info) };
  } catch {
    return null;
  }
}

/** A procedural block (`BEGIN …`, `DO $$ … $$`), or a statement led by IF / WHILE (T-SQL control flow). */
function isControlFlow(sql: string, info: StatementInfo, options: ParseOptions): boolean {
  if (info.kind === 'procedural-body') return true;
  if (info.kind !== 'unknown') return false;
  const start = trimRange(sql, info.range.startOffset, info.range.endOffset, options).startOffset;
  return /^(?:IF|WHILE)\b/i.test(sql.slice(start, start + 6));
}

function statementField(
  { structure, options }: Statement,
  target: ResultColumnTarget,
  snapshot: SchemaSnapshot | null | undefined,
): ResultColumnField {
  if (structure.kind === 'select') return resultColumnWhereField(structure, options, target, snapshot);
  // UPDATE / DELETE … RETURNING / OUTPUT: its WHERE reads the rows of the
  // statement's own tables, where the driver's source column is the name.
  return { kind: 'field', sql: catalogIdentSql(target.sourceColumn ?? target.name, options.engine) };
}

/** The statement's text with `AND <predicate>` (or a new `WHERE <predicate>`) added; null when it cannot be planned. */
function addWherePredicate({ buffer, info, options }: Statement, predicate: string): string | null {
  const source = buffer.parsedDocument.source;
  try {
    const modified = applyStructuralEdit(buffer, info, { op: 'add-where', text: predicate, combinator: 'AND' });
    const lineIdx = buildLineIndex(source);
    const plannedEdits = modified.plannedEdits.map((edit) => onItsOwnLine(edit, source, options.engine, lineIdx));
    return emitSource({ plannedEdits }, source, { engine: options.engine });
  } catch {
    return null;
  }
}

/**
 * The SQL view's layout for a planned insert: a new `WHERE …` starts a line,
 * an `AND …` an indented one, and code that followed on the same line moves
 * to the next — `FROM t\nWHERE a = 1\nORDER BY a`. Text inserted right after
 * a `--` (or MySQL `#`) line comment starts on a new line too, or the comment
 * would swallow it (the closing parenthesis of an OR clause the planner wraps).
 */
function onItsOwnLine(
  edit: PlannedByteEdit,
  source: string,
  engine: SqlEngine,
  lineIdx: LineIndex,
): PlannedByteEdit {
  const at = edit.range.startOffset;
  if (edit.range.endOffset !== at) return edit;
  // The planner already breaks the line after a trailing line comment
  // (`…-- note` + `\nAND …`); lay that out like any other insert.
  let text = edit.replacement.replace(
    /^((?:\r?\n)?\)?)(?:[ \t]+|[ \t]*\r?\n[ \t]*)(WHERE|AND) /,
    (_match, close: string, keyword: string) => `${close}\n${keyword === 'AND' ? '  ' : ''}${keyword} `,
  );
  let end = at;
  if (text !== edit.replacement) {
    const spaces = /^[ \t]*/.exec(source.slice(at))![0];
    if (codeStartsAt(source, at + spaces.length, engine)) {
      text += '\n';
      end = at + spaces.length;
    }
  }
  const comment = at > 0 ? findEnclosingComment(source, at - 1, engine) : null;
  if (comment?.kind === 'line' && comment.end === at && !/^\r?\n/.test(text)) text = `\n${text}`;
  return text === edit.replacement ? edit : { ...edit, range: rangeFromOffsets(lineIdx, at, end), replacement: text };
}

/** Whether SQL code (not a line end, `;` or comment) starts at `i`. */
function codeStartsAt(source: string, i: number, engine: SqlEngine): boolean {
  const c = source[i];
  if (c === undefined || c === '\n' || c === '\r' || c === ';') return false;
  const two = source.slice(i, i + 2);
  return two !== '--' && two !== '/*' && !(c === '#' && engine === 'mysql');
}
